import { useState } from 'react';
import {
  Box,
  Typography,
  ClickAwayListener,
  Tooltip,
  IconButton,
  Grid2 as Grid,
  Chip,
  Stack,
  useTheme,
  darken
} from '@mui/material';
import { Control } from 'react-hook-form';
import { MdInfoOutline } from 'react-icons/md';
import Input from 'components/Common/Input';
import CopyIcon from 'assets/icons/CopyIcon';
import { BACKEND_URL } from 'env';

interface StripeThirdPartyFormProps {
  disabled: boolean;
  control: Control<{ configuration: Record<string, unknown> }>;
}

const WEBHOOK_EVENTS = [
  'checkout.session.completed',
  'customer.updated',
  'invoice.finalised',
  'invoice.payment_succeeded',
  'invoice.voided',
  'charge.refunded'
];

export default function StripeThirdPartyForm({ control, disabled }: StripeThirdPartyFormProps) {
  const theme = useTheme();
  const [copyTooltipOpen, setCopyTooltipOpen] = useState(false);
  const webhookUrl = `${BACKEND_URL}/stripe_webhooks`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(webhookUrl);
    setCopyTooltipOpen(true);
  };

  return (
    <Stack gap={2}>
      <Input
        name='configuration.secret_key'
        control={control}
        label='Secret Key'
        placeholder='Enter Here'
        disabled={disabled}
        required
        fullWidth
        InputLabelProps={{ shrink: true }}
      />
      <Input
        name='configuration.publishable_key'
        control={control}
        label='Publishable Key'
        placeholder='Enter Here'
        disabled={disabled}
        required
        fullWidth
        InputLabelProps={{ shrink: true }}
      />
      <Input
        name='configuration.webhook_secret'
        control={control}
        label='Webhook Secret'
        placeholder='Enter Here'
        disabled={disabled}
        required
        fullWidth
        InputLabelProps={{ shrink: true }}
      />

      <Box
        sx={{
          border: '1px solid #f3f3f3',
          borderRadius: 2,
          p: 2
        }}
      >
        <Typography fontWeight={500} mb={1}>
          Customer Onboarding
        </Typography>
        <Typography variant='body2' color='text.secondary' mb={2}>
          These settings are automatically applied when a new business goes through the onboarding process.
        </Typography>

        <Grid container spacing={2}>
          <Grid size={12}>
            <Input
              name='configuration.product_price_id'
              control={control}
              label='Product Price ID'
              placeholder='Enter Here'
              disabled={disabled}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <Typography variant='caption' color='text.secondary' display='block' mt={0.5}>
              Fetches the one time price from Stripe to charge during onboarding.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Input
              name='configuration.auto_apply_promo_code'
              control={control}
              label='Auto Apply Promo Code'
              placeholder='Enter Promo Code'
              disabled={disabled}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <Typography variant='caption' color='text.secondary' display='block' mt={0.5}>
              Automatically applied as a discount during the onboarding checkout.
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Box
        sx={{
          backgroundColor: theme.palette.primary.light,
          border: `1px solid ${darken(theme.palette.primary.light, 0.1)}`,
          borderRadius: 1,
          p: { xs: 1.5, sm: 2 }
        }}
      >
        <Box display='flex' flexDirection='row' alignItems='center' gap={1} mb={1}>
          <Box
            component='span'
            sx={{
              width: { xs: 20, sm: 24 },
              height: { xs: 20, sm: 24 },
              color: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <MdInfoOutline size={18} />
          </Box>
          <Typography
            fontWeight={500}
            color={theme.palette.primary.main}
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            Stripe Webhook Configuration
          </Typography>
        </Box>
        <Typography color={theme.palette.primary.main} sx={{ fontSize: { xs: '12px', sm: '14px' } }} mb={1}>
          To complete your Stripe integration, create a webhook in your Stripe Dashboard:
        </Typography>

        <Box
          display='flex'
          flexDirection={{ xs: 'column', sm: 'row' }}
          borderRadius={1}
          justifyContent='space-between'
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          gap={{ xs: 1, sm: 0 }}
          sx={{ backgroundColor: 'white', px: { xs: 1, sm: 1.5 } }}
        >
          <Box
            display='flex'
            flexDirection={{ xs: 'column', sm: 'row' }}
            gap={{ xs: 0.5, sm: '3px' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}
          >
            <Typography
              color={theme.palette.primary.main}
              fontWeight={600}
              sx={{ fontSize: { xs: '12px', sm: '14px' } }}
              noWrap
            >
              Webhook URL :
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '12px', sm: '14px' },
                color: theme.palette.primary.main,
                wordBreak: 'break-all'
              }}
            >
              {webhookUrl}
            </Typography>
          </Box>

          <ClickAwayListener onClickAway={() => setCopyTooltipOpen(false)}>
            <Tooltip
              onClose={() => setCopyTooltipOpen(false)}
              open={copyTooltipOpen}
              disableFocusListener
              disableHoverListener
              disableTouchListener
              placement='top'
              title='Copied'
            >
              <IconButton onClick={handleCopy} size='small' sx={{ flexShrink: 0 }}>
                <Box color={theme.palette.primary.main}>
                  <CopyIcon />
                </Box>
              </IconButton>
            </Tooltip>
          </ClickAwayListener>
        </Box>

        <Typography mt={1} color={theme.palette.primary.main} sx={{ fontSize: { xs: '12px', sm: '14px' } }}>
          Make sure to select and activate the following events:
        </Typography>
        <Box display='flex' flexWrap='wrap' mt={1} gap={1}>
          {WEBHOOK_EVENTS.map((event) => (
            <Chip
              key={event}
              label={event}
              size='small'
              sx={{
                border: `1px solid ${theme.palette.primary.main}`,
                bgcolor: 'transparent',
                color: theme.palette.primary.main,
                borderRadius: '40px',
                textTransform: 'none'
              }}
            />
          ))}
        </Box>
      </Box>
    </Stack>
  );
}
